import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, PlusCircle, ShieldCheck, Images, Dog, Ticket } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export const AppNavbar = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="app-navigation"
      className="sticky top-0 z-50 border-b border-[#E8DFD5]/80 bg-[#FBF8F3]/85 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-8">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-3">
          <img src="/img/logo.webp" alt="La Casetta del Bassotto" className="h-10 w-10 rounded-full object-cover" />
          <span className="font-display text-lg font-semibold leading-none tracking-tight text-[#2C2723]">
            Le Nuove Vite
            <span className="block font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-[#8C7B6E]">
              La Casetta del Bassotto
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          <Link
            to="/"
            data-testid="nav-link-gallery"
            className="flex items-center gap-1.5 text-sm font-medium text-[#5C534B] transition-colors hover:text-[#B85328]"
          >
            <Images size={15} /> Galleria
          </Link>
          <Link
            to="/cani"
            data-testid="nav-link-dogs"
            className="flex items-center gap-1.5 text-sm font-medium text-[#5C534B] transition-colors hover:text-[#B85328]"
          >
            <Dog size={15} /> I Cani
          </Link>
          {user && (
            <Link
              to="/condividi"
              data-testid="nav-link-share"
              className="flex items-center gap-1.5 text-sm font-medium text-[#5C534B] transition-colors hover:text-[#B85328]"
            >
              <PlusCircle size={15} /> Condividi
            </Link>
          )}
          {user?.role === "admin" && (
            <>
              <Link
                to="/moderazione"
                data-testid="nav-link-moderation"
                className="flex items-center gap-1.5 text-sm font-medium text-[#5C534B] transition-colors hover:text-[#B85328]"
              >
                <ShieldCheck size={15} /> Moderazione
              </Link>
              <Link
                to="/inviti"
                data-testid="nav-link-invites"
                className="flex items-center gap-1.5 text-sm font-medium text-[#5C534B] transition-colors hover:text-[#B85328]"
              >
                <Ticket size={15} /> Inviti
              </Link>
            </>
          )}
          {user ? (
            <div className="flex items-center gap-3">
              <span data-testid="nav-user-name" className="flex items-center gap-2 text-sm font-semibold text-[#2C2723]">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                ) : (
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#B85328] font-display text-sm text-[#FBF8F3]">
                    {user.name?.[0]?.toUpperCase()}
                  </span>
                )}
                {user.name}
              </span>
              <button
                data-testid="nav-logout-button"
                onClick={handleLogout}
                className="flex items-center gap-1.5 rounded-full border border-[#CFC0B2] px-4 py-2 text-xs font-semibold text-[#5C534B] transition-all hover:border-[#B85328] hover:text-[#B85328]"
              >
                <LogOut size={13} /> Esci
              </button>
            </div>
          ) : (
            <Link
              to="/accedi"
              data-testid="nav-login-button"
              className="rounded-full bg-[#B85328] px-5 py-2.5 text-sm font-semibold text-[#FBF8F3] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#9C421C] hover:shadow-lg"
            >
              Accedi
            </Link>
          )}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="rounded-md p-2 text-[#2C2723] md:hidden"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-[#E8DFD5] bg-[#FBF8F3] px-6 py-4 md:hidden">
          <Link to="/" data-testid="nav-link-gallery-mobile" onClick={() => setOpen(false)} className="block py-3 font-display text-xl font-medium text-[#2C2723]">
            Galleria
          </Link>
          {user && (
            <Link to="/condividi" data-testid="nav-link-share-mobile" onClick={() => setOpen(false)} className="block py-3 font-display text-xl font-medium text-[#2C2723]">
              Condividi un ricordo
            </Link>
          )}
          <Link to="/cani" data-testid="nav-link-dogs-mobile" onClick={() => setOpen(false)} className="block py-3 font-display text-xl font-medium text-[#2C2723]">
            I Cani
          </Link>
          {user?.role === "admin" && (
            <>
              <Link to="/moderazione" data-testid="nav-link-moderation-mobile" onClick={() => setOpen(false)} className="block py-3 font-display text-xl font-medium text-[#2C2723]">
                Moderazione
              </Link>
              <Link to="/inviti" data-testid="nav-link-invites-mobile" onClick={() => setOpen(false)} className="block py-3 font-display text-xl font-medium text-[#2C2723]">
                Inviti
              </Link>
            </>
          )}
          {user ? (
            <button data-testid="nav-logout-button-mobile" onClick={handleLogout} className="mt-2 w-full rounded-full border border-[#CFC0B2] py-3 text-sm font-semibold text-[#2C2723]">
              Esci ({user.name})
            </button>
          ) : (
            <Link to="/accedi" data-testid="nav-login-button-mobile" onClick={() => setOpen(false)} className="mt-2 block w-full rounded-full bg-[#B85328] py-3 text-center text-sm font-semibold text-[#FBF8F3]">
              Accedi
            </Link>
          )}
        </div>
      )}
    </header>
  );
};
